import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Modal, TextField, Stack, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import newsletterImage from '../assets/newsletter-trading.png';
import ServerLogger from '../utils/ClientLogger';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import apiFetch from '../utils/apiFetch';

const DISMISS_COOLDOWN_MS = 1000 * 60 * 60 * 24 * 90; // 90 dager før popup vises igjen etter "lukk"

// Popupen hører hjemme på den offentlige markedsføringsflaten — ingen andre
// steder. App.js monterer den utenfor <Routes>, så uten denne sjekken dukket
// den opp i terminalen, app-skallet og admin, altså for folk som allerede er
// kunder. Gjelder både FlockTrade-landingen (LandingV2) og tenant-landingen
// (Home) — begge ligger på "/".
//
// Juridiske sider (/terms, /privacy, /risk-disclosure, /security) er bevisst
// utelatt: en markedsføringsmodal oppå risikoadvarselen er dårlig skikk.
const LANDING_PATHS = new Set([
  '/',
  '/v2',
  '/about',
  '/contact',
  '/affiliates',
  '/changelog',
  '/pricing',
]);

const NewsletterPopup = () => {
  const { t } = useTranslation(); // Henter oversettelser
  const { pathname } = useLocation();
  const isLandingPage = LANDING_PATHS.has(pathname.replace(/\/+$/, '') || '/');
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isLandingPage) return;

    const lastAction = localStorage.getItem('newsletter_action');
    const lastActionTime = localStorage.getItem('newsletter_action_time');

    if (lastAction === 'subscribed') return;

    const now = new Date().getTime();
    const timeSinceLastAction = lastActionTime ? now - parseInt(lastActionTime, 10) : Infinity;

    if (lastAction === 'dismissed' && timeSinceLastAction < DISMISS_COOLDOWN_MS) {
      return; // Vent lenge før popup-en vises igjen etter at bruker har lukket den
    }

    const timer = setTimeout(() => {
      setOpen(true);
    }, 15000);

    return () => clearTimeout(timer);
  }, [isLandingPage]);

  const handleSubscribe = async () => {
    if (!name || !email) {
      setError(t('newsletter.errorRequired'));
      return;
    }

    try {
      const response = await apiFetch(`${backendUrl}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name, email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || t('newsletter.errorGeneric'));
        return;
      }

      localStorage.setItem('newsletter_action', 'subscribed');
      setOpen(false);
    } catch (error) {
      ServerLogger.error('Error subscribing:', error);
      setError(t('newsletter.errorGeneric'));
    }
  };

  const handleDismiss = () => {
    localStorage.setItem('newsletter_action', 'dismissed');
    localStorage.setItem('newsletter_action_time', new Date().getTime());
    setOpen(false);
  };

  if (!isLandingPage) return null;

  return (
    <Modal
      open={open}
      onClose={handleDismiss}
      aria-labelledby="newsletter-modal-title"
      aria-describedby="newsletter-modal-description"
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          width: '90%',
          maxWidth: 500,
        }}
      >
        <IconButton
          sx={{ position: 'absolute', top: 8, right: 8 }}
          onClick={handleDismiss}
        >
          <CloseIcon />
        </IconButton>

        <Stack spacing={2} alignItems="center">
          <Box
            component="img"
            src={newsletterImage}
            alt="Newsletter illustration"
            sx={{
              width: '100%',
              maxWidth: 400,
              height: 'auto',
              borderRadius: 4,
              mb: 2,
            }}
          />

          <Typography
            id="newsletter-modal-title"
            variant="h5"
            sx={{ fontWeight: 'bold', textAlign: 'center' }}
          >
            {t('newsletter.title')}
          </Typography>

          <Typography
            id="newsletter-modal-description"
            sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}
          >
            {t('newsletter.description')}
          </Typography>

          {error && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center' }}>
              {error}
            </Typography>
          )}

          <TextField
            label={t('newsletter.nameLabel')}
            variant="outlined"
            fullWidth
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label={t('newsletter.emailLabel')}
            type="email"
            variant="outlined"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubscribe}
              startIcon={<EmailIcon />}
            >
              {t('newsletter.subscribe')}
            </Button>
            <Button variant="outlined" color="error" onClick={handleDismiss}>
              {t('newsletter.close')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Modal>
  );
};

export default NewsletterPopup;
